(() => {
  'use strict';

  if (window.__splitflaskRelease3Loaded) return;
  window.__splitflaskRelease3Loaded = true;

  const configElement = document.querySelector('[data-sf-r3-config]');
  const config = {
    recipeProductId: Number(configElement?.dataset.recipeProductId || 9419494195480),
    recipeVariantId: Number(configElement?.dataset.recipeVariantId || 50556794339608),
    protectionProductId: Number(configElement?.dataset.protectionProductId || 0),
    countryCode: String(configElement?.dataset.countryCode || '').toUpperCase(),
    root: window.Shopify?.routes?.root || '/',
  };

  const nativeFetch = window.fetch.bind(window);
  const mutationPattern = /\/cart\/(add|change|update)(?:\.js)?$/;
  let syncQueue = Promise.resolve();
  let checkoutRedirecting = false;
  const viewedModules = new WeakSet();
  let moduleViewObserver;

  const normalizeRoot = (path) => {
    const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
    return `${root}${String(path).replace(/^\//, '')}`;
  };

  const cartUrl = normalizeRoot('cart.js');
  const cartAddUrl = window.routes?.cart_add_url || normalizeRoot('cart/add.js');
  const cartUpdateUrl = window.routes?.cart_update_url || normalizeRoot('cart/update.js');
  const checkoutUrl = normalizeRoot('checkout');

  function track(action, detail = {}) {
    const payload = {
      event: 'splitflask_release_3',
      sf_action: action,
      ...detail,
    };

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (error) {
      // Analytics must never interfere with a storefront action.
    }

    try {
      window.Shopify?.analytics?.publish?.('splitflask_release_3', {
        action,
        ...detail,
      });
    } catch (error) {
      // The Shopify analytics bridge is optional on the storefront.
    }

    document.dispatchEvent(new CustomEvent('splitflask:release3', {
      detail: { action, ...detail },
    }));
  }

  function isRecipeItem(item) {
    return Number(item?.product_id) === config.recipeProductId || Number(item?.variant_id) === config.recipeVariantId;
  }

  function isProtectionItem(item) {
    return config.protectionProductId > 0 && Number(item?.product_id) === config.protectionProductId;
  }

  function isPaidMerchandise(item) {
    if (isRecipeItem(item) || isProtectionItem(item)) return false;
    return Number(item?.final_line_price || 0) > 0;
  }

  function visibleCartState(cartState) {
    if (!Array.isArray(cartState?.items)) {
      return {
        itemCount: Number(cartState?.item_count || 0),
        totalPrice: Number(cartState?.total_price || 0),
      };
    }

    return cartState.items.reduce((state, item) => {
      if (isRecipeItem(item) || isProtectionItem(item)) return state;
      state.itemCount += Number(item.quantity || 0);
      state.totalPrice += Number(item.final_line_price || 0);
      return state;
    }, { itemCount: 0, totalPrice: 0 });
  }

  // Existing theme cart utilities are global functions. Replacing them here
  // keeps the free recipe and optional protection out of visible cart counts.
  window.getCartMerchandiseState = visibleCartState;

  window.updateFreeShipping = async function updateSplitflaskFreeShipping() {
    const freeShippingElements = document.querySelectorAll('shipping-bar');
    if (!freeShippingElements.length) return;

    try {
      const response = await nativeFetch(cartUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const cartState = await response.json();
      const { totalPrice, itemCount } = visibleCartState(cartState);
      const conversionRate = Number(window.Shopify?.currency?.rate || 1);
      const threshold = Math.round(Number(window.theme?.settings?.cart_free_shipping_threshold || 0) * conversionRate);
      const defaultText = window.theme?.settings?.cart_free_shipping_text || '';
      const successText = window.theme?.settings?.cart_free_shipping_text_success || '';
      const moneyFormat = window.theme?.settings?.money_format || '${{amount}}';

      freeShippingElements.forEach((freeShipping) => {
        const textElement = freeShipping.querySelector('.progress-bar__text');
        const progressElement = freeShipping.querySelector('[data-progress-line]');
        if (!textElement || !progressElement) return;

        freeShipping.style.visibility = itemCount === 0 ? 'hidden' : 'visible';

        if (freeShipping.dataset.freeTumblerShipping === 'true') {
          textElement.textContent = 'Free standard US shipping on all tumblers.';
          progressElement.style.width = '100%';
          return;
        }

        if (threshold <= 0 || totalPrice >= threshold) {
          textElement.innerHTML = successText;
          progressElement.style.width = '100%';
          return;
        }

        const remainingAmount = Math.max(0, threshold - totalPrice);
        const moneyMatch = defaultText.match(/<span class="money">(.*)<\/span>/);
        textElement.innerHTML = moneyMatch
          ? defaultText.replace(moneyMatch[1], formatPrice(remainingAmount, moneyFormat))
          : defaultText;
        progressElement.style.width = `calc(${Math.min(100, (totalPrice / threshold) * 100)}% + 2px)`;
      });
    } catch (error) {
      console.error('Splitflask shipping reassurance could not be updated.', error);
    }
  };

  function getRequestUrl(input) {
    try {
      return new URL(typeof input === 'string' ? input : input.url, window.location.origin);
    } catch (error) {
      return null;
    }
  }

  function requestSections(init, responseData) {
    const fromResponse = Object.keys(responseData?.sections || {});
    if (fromResponse.length) return fromResponse;

    let value;
    const body = init?.body;
    if (body instanceof FormData || body instanceof URLSearchParams) {
      value = body.get('sections');
    } else if (typeof body === 'string') {
      try {
        value = JSON.parse(body).sections;
      } catch (error) {
        value = '';
      }
    }

    const parsed = Array.isArray(value) ? value : String(value || '').split(',');
    const sections = parsed.map((section) => section.trim()).filter(Boolean);
    return sections.length ? [...new Set(sections)] : ['cart-drawer', 'cart-counter'];
  }

  function requestSectionsUrl(init) {
    const body = init?.body;
    if (body instanceof FormData || body instanceof URLSearchParams) {
      return String(body.get('sections_url') || window.location.pathname);
    }
    if (typeof body === 'string') {
      try {
        return String(JSON.parse(body).sections_url || window.location.pathname);
      } catch (error) {
        return window.location.pathname;
      }
    }
    return window.location.pathname;
  }

  async function postCart(url, body) {
    const response = await nativeFetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });
    const data = await response.clone().json();
    if (!response.ok || data?.status || data?.errors) {
      throw new Error(data?.description || data?.message || 'The cart could not be updated.');
    }
    return { response, data };
  }

  async function fetchCart() {
    const response = await nativeFetch(cartUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('The cart could not be read.');
    return response.json();
  }

  async function syncRecipeBonus({ sections = ['cart-drawer', 'cart-counter'], sectionsUrl = window.location.pathname } = {}) {
    const cartState = await fetchCart();
    const recipeItems = cartState.items.filter(isRecipeItem);
    const shouldIncludeRecipe = cartState.items.some(isPaidMerchandise);
    const updates = {};
    let changed = false;
    let action = '';

    if (shouldIncludeRecipe && recipeItems.length === 0) {
      await postCart(cartAddUrl, {
        items: [{
          id: config.recipeVariantId,
          quantity: 1,
          properties: { _splitflask_bonus: 'Recipe book' },
        }],
      });
      changed = true;
      action = 'recipe_bonus_added';
    } else if (shouldIncludeRecipe && recipeItems.length > 0) {
      recipeItems.forEach((item, index) => {
        const desiredQuantity = index === 0 ? 1 : 0;
        if (Number(item.quantity) !== desiredQuantity) {
          updates[item.key] = desiredQuantity;
          changed = true;
        }
      });
      if (changed) action = 'recipe_bonus_normalized';
    } else if (!shouldIncludeRecipe && recipeItems.length > 0) {
      recipeItems.forEach((item) => {
        updates[item.key] = 0;
      });
      changed = true;
      action = 'recipe_bonus_removed';
    }

    if (Object.keys(updates).length) {
      await postCart(cartUpdateUrl, { updates });
    }

    if (!changed) return null;

    const finalResult = await postCart(cartUpdateUrl, {
      updates: {},
      sections: [...new Set(sections)],
      sections_url: sectionsUrl,
    });

    track(action);
    return finalResult;
  }

  function enqueueRecipeSync(options) {
    const task = syncQueue.then(() => syncRecipeBonus(options));
    syncQueue = task.catch(() => null);
    return task;
  }

  async function parsedResponse(response) {
    try {
      return await response.clone().json();
    } catch (error) {
      return null;
    }
  }

  // Keep recipe-book state atomic with every Ajax cart mutation. If the
  // bonus changes, callers receive a fresh full-cart response and never
  // render an orphaned or duplicated recipe line.
  window.fetch = async function splitflaskFetch(input, init) {
    const response = await nativeFetch(input, init);
    const url = getRequestUrl(input);
    if (!url || url.origin !== window.location.origin || !mutationPattern.test(url.pathname) || !response.ok) {
      return response;
    }

    const responseData = await parsedResponse(response);
    if (!responseData || responseData.status || responseData.errors) return response;

    try {
      const syncResult = await enqueueRecipeSync({
        sections: requestSections(init, responseData),
        sectionsUrl: requestSectionsUrl(init),
      });
      return syncResult?.response || response;
    } catch (error) {
      track('recipe_bonus_error', { context: 'cart_mutation' });
      console.error('The complimentary recipe book could not be synchronized.', error);
      return response;
    }
  };

  function renderCartResponse(cartData) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.renderContents && cartData?.sections) {
      drawer.renderContents(cartData, false);
    }
    if (typeof window.updateCartCounters === 'function') window.updateCartCounters();
    window.updateFreeShipping();
  }

  async function initialRecipeSync() {
    try {
      const result = await enqueueRecipeSync();
      if (result?.data) renderCartResponse(result.data);
    } catch (error) {
      track('recipe_bonus_error', { context: 'initial_sync' });
      console.error('The complimentary recipe book could not be synchronized.', error);
    }
  }

  function initializeInterestForms() {
    document.querySelectorAll('[data-sf-r3-restock]').forEach((restock) => {
      const select = restock.querySelector('[data-sf-r3-interest-select]');
      const tagsInput = restock.querySelector('[data-sf-r3-interest-tags]');
      if (!select || !tagsInput) return;

      select.addEventListener('change', () => {
        const baseTags = tagsInput.value
          .split(',')
          .filter((tag) => !tag.trim().startsWith('variant-'));
        tagsInput.value = [...baseTags, select.value].join(',');
      });
    });

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.classList.contains('sf-r3-signup')) return;
      const list = form.closest('[data-sf-r3-page="40oz-waitlist"]') ? '40oz' : 'restock';
      const selectedInterest = form.querySelector('[data-sf-r3-interest-select]')?.value || '';
      track('waitlist_submitted', { list, interest: selectedInterest });
    });

    document.querySelectorAll('.sf-r3-signup__success, .sf-r3-waitlist__success').forEach((success) => {
      const list = success.closest('[data-sf-r3-page="40oz-waitlist"]') ? '40oz' : 'restock';
      track('waitlist_confirmed', { list });
    });
  }

  function initializeClickTracking() {
    document.addEventListener('click', (event) => {
      const target = event.target.closest('[data-sf-r3-track]');
      if (!target) return;
      track('link_clicked', { destination: target.dataset.sfR3Track || '' });
    });
  }

  function moduleName(element) {
    return element.dataset.sfR3Page || (element.hasAttribute('data-sf-r3-capacity') ? 'capacity' : 'cart_reassurance');
  }

  function observeViewModules(root = document) {
    const elements = root.matches?.('[data-sf-r3-cart-reassurance], [data-sf-r3-capacity], [data-sf-r3-page="40oz-waitlist"]')
      ? [root]
      : [...root.querySelectorAll?.('[data-sf-r3-cart-reassurance], [data-sf-r3-capacity], [data-sf-r3-page="40oz-waitlist"]') || []];

    elements.forEach((element) => {
      if (viewedModules.has(element)) return;
      viewedModules.add(element);
      if (moduleViewObserver) {
        moduleViewObserver.observe(element);
      } else {
        track('module_viewed', { module: moduleName(element) });
      }
    });
  }

  function initializeViewTracking() {
    if ('IntersectionObserver' in window) {
      moduleViewObserver = new window.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          track('module_viewed', { module: moduleName(entry.target) });
          moduleViewObserver.unobserve(entry.target);
        });
      }, { threshold: 0.35 });
    }

    observeViewModules();

    const cartDrawerSection = document.querySelector('#shopify-section-cart-drawer');
    if (cartDrawerSection && 'MutationObserver' in window) {
      const mutationObserver = new window.MutationObserver((records) => {
        records.forEach((record) => {
          record.addedNodes.forEach((node) => {
            if (node.nodeType === 1) observeViewModules(node);
          });
        });
      });
      mutationObserver.observe(cartDrawerSection, { childList: true, subtree: true });
    }
  }

  function initializeCheckoutGuard() {
    document.addEventListener('submit', async (event) => {
      const submitter = event.submitter;
      if (checkoutRedirecting || submitter?.name !== 'checkout') return;

      event.preventDefault();
      checkoutRedirecting = true;
      submitter.disabled = true;
      submitter.setAttribute('aria-disabled', 'true');

      try {
        await enqueueRecipeSync();
      } catch (error) {
        track('recipe_bonus_error', { context: 'checkout_guard' });
      } finally {
        window.location.assign(checkoutUrl);
      }
    }, true);
  }

  function initialize() {
    initializeInterestForms();
    initializeClickTracking();
    initializeViewTracking();
    initializeCheckoutGuard();
    window.updateFreeShipping();
    initialRecipeSync();
  }

  window.SplitflaskRelease3 = {
    syncRecipeBonus: () => enqueueRecipeSync(),
    track,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
